import { Popover, PopoverButton, PopoverPanel } from "@headlessui/react";
import { LinkIcon } from "@heroicons/react/24/outline";
import { multiFieldPrompt } from "components/ui/fancy-multi-field-dialog";
import React from "react";
import {
    adjustLinkSelection,
    clearFormat,
    insertLink,
    setAlignment,
    setBackgroundColor,
    setFontSize,
    setIndentation,
    setTextColor,
    toggleBold,
    toggleBullet,
    toggleItalic,
    toggleNumbering,
    toggleUnderline,
} from "roosterjs";
import { EditorAdapter } from "roosterjs-editor-adapter";
import { ResponsiveMode } from "store/reducers/responsive";
import { useResponsiveMode } from "store/selectors/hooks";
import { ColorPickerPanel } from "./color-picker-panel";
import {
    AlignCenterIcon,
    AlignLeftIcon,
    AlignRightIcon,
    BoldIcon,
    ClearFormattingIcon,
    HighlightIcon,
    IndentDecreaseIcon,
    IndentIncreaseIcon,
    ItalicIcon,
    ListIcon,
    ListNumbersIcon,
    TextColorIcon,
    TextSizeIcon,
    TriangleDownIcon,
    UnderlineIcon,
} from "./ribbon-icons";
import { TextSizePickerPanel } from "./text-size-picker-panel";

const clearAllFormatting = (editor: EditorAdapter) => {
    const range = editor.getSelectionRange();
    if (!range || range.collapsed) {
        return;
    }

    clearFormat(editor);

    const doc = editor.getDocument();
    const selection = doc.getSelection();

    if (!selection || selection.rangeCount === 0) {
        return;
    }

    const selectedRange = selection.getRangeAt(0);

    const findElementsInRange = (range: Range): Element[] => {
        const elements: Element[] = [];
        const container = range.commonAncestorContainer;

        const traverse = (node: Node) => {
            if (node.nodeType === Node.ELEMENT_NODE && range.intersectsNode(node)) {
                elements.push(node as Element);
            }
            node.childNodes.forEach(child => traverse(child));
        };

        traverse(
            container.nodeType === Node.ELEMENT_NODE
                ? container
                : container.parentElement!,
        );
        return elements;
    };

    const elements = findElementsInRange(selectedRange);

    elements.forEach(element => {
        element.removeAttribute("style");
        element.removeAttribute("color");
        element.removeAttribute("face");
        element.removeAttribute("size");
    });

    const elementsToUnwrap = elements.filter(element => {
        const tagName = element.tagName.toLowerCase();
        return ["a", "b", "strong", "i", "em", "u", "font", "span", "mark"].includes(
            tagName,
        );
    });

    elementsToUnwrap.forEach(element => {
        const parent = element.parentNode;
        if (parent) {
            while (element.firstChild) {
                parent.insertBefore(element.firstChild, element);
            }
            parent.removeChild(element);
        }
    });
};

interface Props {
    editor: EditorAdapter;
    popoverAnchor?: "top" | "bottom";
}

export const FormattingButtons: React.FC<Props> = ({
    editor,
    popoverAnchor = "bottom",
}) => {
    const responsiveMode = useResponsiveMode();
    const isMobile =
        responsiveMode === ResponsiveMode.small ||
        responsiveMode === ResponsiveMode.medium;
    const boldHandler = React.useCallback(
        (e: React.MouseEvent<HTMLButtonElement>) => {
            e.preventDefault();
            toggleBold(editor);
        },
        [editor],
    );

    const italicHandler = React.useCallback(
        (e: React.MouseEvent<HTMLButtonElement>) => {
            e.preventDefault();
            toggleItalic(editor);
        },
        [editor],
    );

    const underlineHandler = React.useCallback(
        (e: React.MouseEvent<HTMLButtonElement>) => {
            e.preventDefault();
            toggleUnderline(editor);
        },
        [editor],
    );

    const alignLeftHandler = React.useCallback(
        (e: React.MouseEvent<HTMLButtonElement>) => {
            e.preventDefault();
            setAlignment(editor, "left");
        },
        [editor],
    );

    const alignCenterHandler = React.useCallback(
        (e: React.MouseEvent<HTMLButtonElement>) => {
            e.preventDefault();
            setAlignment(editor, "center");
        },
        [editor],
    );

    const alignRightHandler = React.useCallback(
        (e: React.MouseEvent<HTMLButtonElement>) => {
            e.preventDefault();
            setAlignment(editor, "right");
        },
        [editor],
    );

    const bulletHandler = React.useCallback(
        (e: React.MouseEvent<HTMLButtonElement>) => {
            e.preventDefault();
            toggleBullet(editor);
        },
        [editor],
    );

    const numberHandler = React.useCallback(
        (e: React.MouseEvent<HTMLButtonElement>) => {
            e.preventDefault();
            toggleNumbering(editor);
        },
        [editor],
    );

    const increaseIndentHandler = React.useCallback(
        (e: React.MouseEvent<HTMLButtonElement>) => {
            e.preventDefault();
            setIndentation(editor, "indent");
        },
        [editor],
    );

    const decreaseIndentHandler = React.useCallback(
        (e: React.MouseEvent<HTMLButtonElement>) => {
            e.preventDefault();
            setIndentation(editor, "outdent");
        },
        [editor],
    );

    const textSizeHandler = React.useCallback(
        (fontSize: string) => {
            setFontSize(editor, fontSize);
        },
        [editor],
    );

    const backgroundColorHandler = React.useCallback(
        (color: string) => {
            setBackgroundColor(editor, color);
        },
        [editor],
    );

    const foregroundColorHandler = React.useCallback(
        (color: string) => {
            setTextColor(editor, color);
        },
        [editor],
    );

    const clearFormatHandler = React.useCallback(
        (e: React.MouseEvent<HTMLButtonElement>) => {
            e.preventDefault();
            clearAllFormatting(editor);
        },
        [editor],
    );

    const insertLinkHandler = React.useCallback(
        async (e: React.MouseEvent<HTMLButtonElement>) => {
            e.preventDefault();
            const [text, href] = adjustLinkSelection(editor);
            const { values, button } = await multiFieldPrompt({
                title: href && href.trim() !== "" ? "Edit Link" : "Insert link",
                fields: [
                    { name: "text", label: "Link text" },
                    { name: "href", label: "Link location" },
                ],
                currentValues: { href, text },
                buttons: [
                    { text: "OK", primary: true, callbackName: "ok" },
                    { text: "Cancel", primary: false, callbackName: "cancel" },
                ],
            });
            if (button === "ok" && values.href && values.text) {
                insertLink(editor, values.href, values.href, values.text);
            }
        },
        [editor],
    );

    return (
        <div className="flex items-center gap-2">
            <button
                type="button"
                className="rounded-sm hover:bg-gray-400"
                onClick={boldHandler}>
                <BoldIcon className="w-5 h-5" />
            </button>
            <button
                type="button"
                className="rounded-sm hover:bg-gray-400"
                onClick={italicHandler}>
                <ItalicIcon className="w-5 h-5" />
            </button>
            <button
                type="button"
                className="rounded-sm hover:bg-gray-400"
                onClick={underlineHandler}>
                <UnderlineIcon className="w-5 h-5" />
            </button>
            <button
                type="button"
                className="rounded-sm hover:bg-gray-400"
                onClick={insertLinkHandler}>
                <LinkIcon className="w-5 h-5" />
            </button>
            {isMobile ? null : (
                <>
                    <Popover>
                        <div className="flex items-center">
                            <PopoverButton className="rounded-sm hover:bg-gray-400">
                                <div className="flex items-center">
                                    <TextSizeIcon className="w-5 h-5" />
                                    <TriangleDownIcon className="w-1.5 h-1.5" />
                                </div>
                            </PopoverButton>
                        </div>
                        <PopoverPanel
                            anchor={popoverAnchor}
                            transition
                            className="rounded-md shadow-lg bg-white ring-1 ring-gray-200 ring-opacity-5 divide-y divide-gray-100 focus:outline-none z-10 transition duration-100 data-[closed]:opacity-0 data-[enter]:opacity-100">
                            <TextSizePickerPanel
                                editor={editor}
                                current={e =>
                                    e.getStyleBasedFormatState().fontSize ||
                                    e.getDefaultFormat().fontSize
                                }
                                onTextSizeSelected={textSizeHandler}
                            />
                        </PopoverPanel>
                    </Popover>
                    <Popover>
                        <div className="flex items-center">
                            <PopoverButton className="rounded-sm hover:bg-gray-400">
                                <div className="flex items-center">
                                    <HighlightIcon className="w-5 h-5" />
                                    <TriangleDownIcon className="w-1.5 h-1.5" />
                                </div>
                            </PopoverButton>
                        </div>
                        <PopoverPanel
                            anchor={popoverAnchor}
                            transition
                            className="rounded-md shadow-lg bg-white ring-1 ring-gray-200 ring-opacity-5 divide-y divide-gray-100 focus:outline-none z-10 transition duration-100 data-[closed]:opacity-0 data-[enter]:opacity-100">
                            <ColorPickerPanel
                                editor={editor}
                                current={e =>
                                    e.getStyleBasedFormatState().backgroundColor ||
                                    e.getDefaultFormat().backgroundColor
                                }
                                label="Background color"
                                onColorSelected={backgroundColorHandler}
                            />
                        </PopoverPanel>
                    </Popover>
                    <Popover>
                        <div className="flex items-center">
                            <PopoverButton className="rounded-sm hover:bg-gray-400">
                                <div className="flex items-center">
                                    <TextColorIcon className="w-5 h-5" />
                                    <TriangleDownIcon className="w-1.5 h-1.5" />
                                </div>
                            </PopoverButton>
                        </div>
                        <PopoverPanel
                            anchor={popoverAnchor}
                            transition
                            className="rounded-md shadow-lg bg-white ring-1 ring-gray-200 ring-opacity-5 divide-y divide-gray-100 focus:outline-none z-10 transition duration-100 data-[closed]:opacity-0 data-[enter]:opacity-100">
                            <ColorPickerPanel
                                editor={editor}
                                current={e =>
                                    e.getStyleBasedFormatState().textColor ||
                                    e.getDefaultFormat().textColor
                                }
                                label="Text color"
                                onColorSelected={foregroundColorHandler}
                            />
                        </PopoverPanel>
                    </Popover>
                </>
            )}
            <button
                type="button"
                className="rounded-sm hover:bg-gray-400"
                onClick={clearFormatHandler}>
                <ClearFormattingIcon className="w-5 h-5" />
            </button>
            <button
                type="button"
                className="rounded-sm hover:bg-gray-400"
                onClick={alignLeftHandler}>
                <AlignLeftIcon className="w-5 h-5" />
            </button>
            <button
                type="button"
                className="rounded-sm hover:bg-gray-400"
                onClick={alignCenterHandler}>
                <AlignCenterIcon className="w-5 h-5" />
            </button>
            <button
                type="button"
                className="rounded-sm hover:bg-gray-400"
                onClick={alignRightHandler}>
                <AlignRightIcon className="w-5 h-5" />
            </button>
            <button
                type="button"
                className="rounded-sm hover:bg-gray-400"
                onClick={bulletHandler}>
                <ListIcon className="w-5 h-5" />
            </button>
            <button
                type="button"
                className="rounded-sm hover:bg-gray-400"
                onClick={numberHandler}>
                <ListNumbersIcon className="w-5 h-5" />
            </button>
            <button
                type="button"
                className="rounded-sm hover:bg-gray-400"
                onClick={increaseIndentHandler}>
                <IndentIncreaseIcon className="w-5 h-5" />
            </button>
            <button
                type="button"
                className="rounded-sm hover:bg-gray-400"
                onClick={decreaseIndentHandler}>
                <IndentDecreaseIcon className="w-5 h-5" />
            </button>
        </div>
    );
};
